import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { StockDataMA } from '../../../dto/StockDataMA';
import { ModalButtons } from '../../../../../../shared/component/modal-container/modal-container.component';
import { firstValueFrom, Subject } from 'rxjs';
import { MarketAnalysisTickerService } from '../../../services/MarketAnalysisTickerService';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NOTIFICATIONS, NotificationsService } from '../../../../../../../core/service/notifications.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CheckBoxSelector } from '../../../../../../shared/component/checkbox-selector/checkbox-selector.component';
import { StockNameTicker } from '../../../../../../company-performance/companies-stocks/dto/StockNameTicker';
import { Currency } from '../../../../../../company-performance/companies-stocks/enum/Currency';

@Component({
  selector: 'app-recovery-ticker-modal',
  templateUrl: './recovery-ticker-modal.component.html',
  styleUrls: ['./recovery-ticker-modal.component.scss'],
})
export class RecoveryTickerModalComponent implements OnChanges, OnDestroy, OnInit {
  @Input() stockDataMA: StockDataMA;
  @Input() allSectors: Sectors[];
  @Output() closeEmitter = new EventEmitter<boolean>();
  selectedSectorsArray: CheckBoxSelector[] = [];
  isEditMode = false;
  stockMATickerForm: MAStockTickerFormGroup;
  foundTickers: StockNameTicker[];
  showFetchedCompaniesFromBackend = false;
  modalButtons: ModalButtons = {
    close: true,
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  private _destroyStream: Subject<void> = new Subject<void>();

  constructor(
    protected marketAnalysisTickerService: MarketAnalysisTickerService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private notificationsService: NotificationsService,
  ) {}

  ngOnChanges(): void {
    this.initForm();
  }

  async selectTickerFromList(ticker: StockNameTicker): Promise<void> {
    this.stockMATickerForm.disable();
    this.stockMATickerForm.controls.tickerName.enable();
    this.stockMATickerForm.controls.sectors.enable();
    this.foundTickers = [];
    this.stockDataMA = await firstValueFrom(this.marketAnalysisTickerService.findMAInfoForModal(ticker.symbol));
    this.stockMATickerForm.controls.tickerName.patchValue(this.stockDataMA.tickerName, { emitEvent: false });
    this.stockMATickerForm.controls.companyName.patchValue(ticker.name);
    this.stockMATickerForm.controls.enterpriseValue.patchValue(this.stockDataMA.enterpriseValue);
    this.stockMATickerForm.controls.annualRevenue.patchValue(this.stockDataMA.annualRevenue);
    this.stockMATickerForm.controls.revenueIndex.patchValue(this.stockDataMA.revenueIndex);
    this.stockMATickerForm.controls.revenueGrowth.patchValue(this.stockDataMA.revenueGrowth);
    this.stockMATickerForm.controls.period.patchValue(this.stockDataMA.period);
    this.stockMATickerForm.controls.currencyTicker.patchValue(this.stockDataMA.currencyTicker);
    this.stockMATickerForm.controls.sectors.patchValue(this.stockDataMA.sectors);
  }

  ngOnInit(): void {
    this.isEditMode = Boolean(this.stockDataMA?.tickerName);
    this.stockDataMA ??= new StockDataMA();
    this.initForm();
    this.stockMATickerForm.controls.tickerName.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this._destroyStream),
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
    ).subscribe(async (pattern: string) => {
      this.showFetchedCompaniesFromBackend = false;
      this.foundTickers = [];
      if (!pattern || pattern?.length < 2) {
        return;
      }

      this.foundTickers = await firstValueFrom(this.marketAnalysisTickerService.searchStocks(pattern));
      this.showFetchedCompaniesFromBackend = true;
    });
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async close(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      const sectorsFormArray = this.stockMATickerForm.controls.sectors;
      const sectorsList: Sectors[] = sectorsFormArray.controls.map((control) => {
        return {
          id: control.controls.id.value,
          sector: control.controls.sector.value,
        };
      });
      const tickerIndustriesDTO: TickerIndustriesDTO = {
        tickerName: this.stockDataMA.tickerName,
        sectorsList,
      };
      if (this.isEditMode) {
        await firstValueFrom(this.marketAnalysisTickerService.updateTickerIndustries(tickerIndustriesDTO));
      } else {
        await firstValueFrom(this.marketAnalysisTickerService.addTickerToMA(tickerIndustriesDTO));
      }
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    }
    this.closeEmitter.emit(isSuccessful);
  }

  resetForm(): void {
    this.stockDataMA = undefined;
    this.stockMATickerForm.reset();
    this.stockMATickerForm.disable();
    this.stockMATickerForm.controls.tickerName.enable();
  }

  setFMSSectors(selectedIndustries: CheckBoxSelector[]): void {
    this.stockMATickerForm.controls.sectors.clear();
    selectedIndustries.filter((sector) => sector.chosen).forEach((sector) => {
      this.stockMATickerForm.controls.sectors.push(this.createSectorForm({
        id: sector.itemId,
        sector: sector.item,
      }));
    });
  }

  private initForm(): void {
    this.stockMATickerForm = this.fb.group({
      tickerName: new FormControl<string>({ value: this.stockDataMA?.tickerName, disabled: this.isEditMode }),
      companyName: new FormControl<string>({ value: this.stockDataMA?.companyName, disabled: true }),
      enterpriseValue: new FormControl<number>({ value: this.stockDataMA?.enterpriseValue, disabled: true }),
      annualRevenue: new FormControl<number>({ value: this.stockDataMA?.annualRevenue, disabled: true }),
      revenueIndex: new FormControl<number>({ value: this.stockDataMA?.revenueIndex, disabled: true }),
      revenueGrowth: new FormControl<number>({ value: this.stockDataMA?.revenueGrowth, disabled: true }),
      period: new FormControl<Date>({ value: this.stockDataMA?.period, disabled: true }),
      currencyTicker: new FormControl<Currency>({ value: this.stockDataMA?.currencyTicker, disabled: true }),
      sectors: this.setSectors(),
    });
  }

  private setSectors(): FormArray {
    this.selectedSectorsArray = this.allSectors.map((sector) => {
      const isSelected = this.stockDataMA?.sectors?.some((stockSector) => stockSector.id === sector.id);
      return {
        itemId: sector.id,
        item: sector.sector,
        chosen: Boolean(isSelected),
        isHidden: false,
        disabled: false,
      };
    });
    if (!this.stockDataMA || !this.stockDataMA.sectors) {
      return this.fb.array([]);
    }

    return this.fb.array(
      this.stockDataMA.sectors
        .map((sector) => this.createSectorForm(sector)),
    );
  }

  private createSectorForm(sectorDTO: Sectors): SectorFMSForm {
    return this.fb.group({
      id: new FormControl(sectorDTO.id),
      sector: new FormControl(sectorDTO.sector),
    });
  }
}

type MAStockTickerFormGroup = FormGroup<{
  tickerName: FormControl<string>;
  companyName: FormControl<string>;
  enterpriseValue: FormControl<number>;
  revenueIndex: FormControl<number>;
  revenueGrowth: FormControl<number>;
  annualRevenue: FormControl<number>;
  period: FormControl<Date>;
  currencyTicker: FormControl<Currency>;
  sectors: FormArray<SectorFMSForm>;
}>;

export class Sectors {
  id: number;
  sector: string;
}
export class TickerIndustriesDTO {
  tickerName: string;
  sectorsList: Sectors[];
}

type SectorFMSForm = FormGroup<{
  id: FormControl<number>;
  sector: FormControl<string>;
}>;
